import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import _ from 'lodash';

import { MAX_GAP, MIN_GAP, MORE_BUTTON_WIDTH_RESERVE, RESIZE_DEBOUNCE_MS } from './constants';

/**
 * Hook that dynamically calculates how many child items fit
 * within a container before overflowing, using a minimum gap
 * for measurement and returning a computed gap that distributes
 * remaining space evenly between visible items.
 *
 * Requires two refs:
 * - `measureRef`: attached to a hidden container that renders ALL items
 *    (used to measure individual item widths).
 * - `containerRef`: attached to the visible container whose width
 *    determines how many items can be displayed.
 *
 * Optionally accepts `reserveRef`: attach it to a hidden element rendering the
 * overflow trigger (e.g. a "More"/"Other" tab) to reserve that element's
 * measured width instead of the fixed `MORE_BUTTON_WIDTH_RESERVE` fallback —
 * needed when the trigger is a variable-width text tab rather than an icon button.
 *
 * Uses a ResizeObserver on both the visible container and the hidden
 * measurement container to recalculate whenever sizes change.
 *
 * @param {{ minVisibleItems: number }} options
 * @param {number} options.minVisibleItems - Minimum items to always show (default: 1)
 * @returns {{ measureRef: Function, containerRef: Function, reserveRef: Function, maxVisibleItems: number, gap: number, measuredChildCount: number }}
 */
const useOverflowCount = ({ minVisibleItems = 1 } = {}) => {
  const [result, setResult] = useState({
    maxVisibleItems: Infinity,
    gap: MAX_GAP,
    measuredChildCount: 0,
  });
  const measureNodeRef = useRef(null);
  const containerNodeRef = useRef(null);
  const reserveNodeRef = useRef(null);
  const observerRef = useRef(null);

  const calculateVisibleCount = useCallback(() => {
    const measureContainer = measureNodeRef.current;
    const visibleContainer = containerNodeRef.current;

    if (!measureContainer || !visibleContainer) {
      return;
    }

    const children = Array.from(measureContainer.children);

    if (!children.length) {
      return;
    }

    const containerWidth = visibleContainer.clientWidth;
    const totalItems = children.length;
    const childWidths = children.map(child => child.offsetWidth);
    const totalContentWidth = childWidths.reduce((sum, w) => sum + w, 0);

    // Check if ALL items fit with minimum gap (no more button needed)
    const allItemsMinWidth = totalContentWidth + (totalItems - 1) * MIN_GAP;

    if (allItemsMinWidth <= containerWidth) {
      // All items fit — compute a distributed gap (capped at MAX_GAP)
      const distributedGap =
        totalItems > 1
          ? Math.min(MAX_GAP, (containerWidth - totalContentWidth) / (totalItems - 1))
          : 0;

      setResult(prev => {
        const newGap = Math.round(distributedGap);

        return prev.maxVisibleItems === totalItems &&
          prev.gap === newGap &&
          prev.measuredChildCount === totalItems
          ? prev
          : { maxVisibleItems: totalItems, gap: newGap, measuredChildCount: totalItems };
      });

      return;
    }

    // Not all items fit — reserve space for the overflow trigger. When a reserveRef element is
    // provided, use its measured width plus MAX_GAP: the visible container renders one uniform
    // distributed gap (capped at MAX_GAP) between every element including the last shown item and
    // the trigger, and the fit loop below counts gaps only between visible items — never that
    // trailing gap. Reserving the worst-case gap keeps the trigger from clipping. Otherwise fall
    // back to the fixed icon-button reserve.
    const reserveWidth = reserveNodeRef.current?.offsetWidth
      ? reserveNodeRef.current.offsetWidth + MAX_GAP
      : MORE_BUTTON_WIDTH_RESERVE;
    const availableWidth = containerWidth - reserveWidth;

    const { count } = childWidths.reduce(
      (acc, width) => {
        if (acc.done) {
          return acc;
        }

        const gapWidth = acc.count > 0 ? MIN_GAP : 0;
        const nextWidth = acc.usedWidth + width + gapWidth;

        if (nextWidth > availableWidth) {
          return { ...acc, done: true };
        }

        return { usedWidth: nextWidth, count: acc.count + 1, done: false };
      },
      { usedWidth: 0, count: 0, done: false },
    );

    const visibleCount = Math.max(count, minVisibleItems);

    // Compute distributed gap for visible items
    const visibleContentWidth = childWidths.slice(0, visibleCount).reduce((sum, w) => sum + w, 0);
    const distributedGap =
      visibleCount > 1
        ? Math.min(MAX_GAP, (availableWidth - visibleContentWidth) / (visibleCount - 1))
        : 0;

    setResult(prev => {
      const newGap = Math.max(MIN_GAP, Math.round(distributedGap));

      return prev.maxVisibleItems === visibleCount &&
        prev.gap === newGap &&
        prev.measuredChildCount === totalItems
        ? prev
        : { maxVisibleItems: visibleCount, gap: newGap, measuredChildCount: totalItems };
    });
  }, [minVisibleItems]);

  const debouncedCalculate = useMemo(
    () => _.debounce(() => calculateVisibleCount(), RESIZE_DEBOUNCE_MS),
    [calculateVisibleCount],
  );

  const setupObserver = useCallback(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    if (!measureNodeRef.current && !containerNodeRef.current) {
      return;
    }

    observerRef.current = new ResizeObserver(() => {
      debouncedCalculate();
    });

    if (containerNodeRef.current) {
      observerRef.current.observe(containerNodeRef.current);
    }

    if (measureNodeRef.current) {
      observerRef.current.observe(measureNodeRef.current);
    }

    if (reserveNodeRef.current) {
      observerRef.current.observe(reserveNodeRef.current);
    }
  }, [debouncedCalculate]);

  const measureRef = useCallback(
    node => {
      measureNodeRef.current = node;

      if (!node) {
        return;
      }

      calculateVisibleCount();
      setupObserver();
    },
    [calculateVisibleCount, setupObserver],
  );

  const containerRef = useCallback(
    node => {
      containerNodeRef.current = node;

      if (!node) {
        return;
      }

      calculateVisibleCount();
      setupObserver();
    },
    [calculateVisibleCount, setupObserver],
  );

  const reserveRef = useCallback(
    node => {
      reserveNodeRef.current = node;

      if (!node) {
        return;
      }

      calculateVisibleCount();
      setupObserver();
    },
    [calculateVisibleCount, setupObserver],
  );

  // Set up and tear down the observer symmetrically in one effect. A cleanup-only effect leaves the
  // observer disconnected after StrictMode's dev mount→unmount→mount double-invoke (the ref callbacks
  // create it, the simulated unmount's cleanup disconnects it, and nothing recreates it), which
  // silently freezes maxVisibleItems/gap at the first measurement so live resizes never recalculate.
  useEffect(() => {
    setupObserver();

    return () => {
      observerRef.current?.disconnect();
      debouncedCalculate.cancel();
    };
  }, [debouncedCalculate, setupObserver]);

  return {
    measureRef,
    containerRef,
    reserveRef,
    maxVisibleItems: result.maxVisibleItems,
    gap: result.gap,
    measuredChildCount: result.measuredChildCount,
  };
};

export default useOverflowCount;
